import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchJson, postJson } from "./client";
import type {
  CreateFuelStationInput,
  FuelStation,
  FuelStationsResponse,
} from "./types";

const fuelStationsKey = ["fuel-stations"] as const;

export function useFuelStations() {
  return useQuery({
    queryKey: fuelStationsKey,
    queryFn: () => fetchJson<FuelStationsResponse>("/api/fuel-stations"),
  });
}

export function useCreateFuelStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFuelStationInput) =>
      postJson<{ station: FuelStation }>("/api/fuel-stations", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fuelStationsKey });
    },
  });
}

export function useDeleteFuelStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchJson<{ ok: true }>(`/api/fuel-stations/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fuelStationsKey });
    },
  });
}

export function useDeleteFuelStations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.all(
        ids.map((id) =>
          fetchJson<{ ok: true }>(`/api/fuel-stations/${id}`, {
            method: "DELETE",
          }),
        ),
      );
      return { ok: true as const, deleted: ids.length };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fuelStationsKey });
    },
  });
}
